import { useEffect, useRef, useState } from 'react'
import { Mic, Trash2, Send } from 'lucide-react'
import { toast } from '../../stores/uiStore'

/**
 * Web sesli mesaj kaydı — MediaRecorder + AnalyserNode.
 *
 * Dalga formu KAYIT ANINDA örneklenir ve mobildeki ile AYNI biçimde (40 haneli
 * 0-9 dizisi) üretilir; iki istemci aynı sesi farklı çizerse kullanıcı veriye
 * güvenmez.
 *
 * Kayıt bittiğinde `onDone(file, durationMs, waveform)` çağrılır. Vazgeçilirse
 * `onCancel()` — blob tutulmaz.
 */
export default function VoiceRecorder({ onDone, onCancel }) {
  const [ms, setMs] = useState(0)
  const [levels, setLevels] = useState([])
  const mediaRef = useRef(null)
  const chunksRef = useRef([])
  const rafRef = useRef(null)
  const audioCtxRef = useRef(null)
  const streamRef = useRef(null)
  const startedRef = useRef(0)
  const levelsRef = useRef([])
  const doneRef = useRef(false)

  // 5 dakika üst sınır — mobil kayıtla aynı, 25MB ek sınırının altında kalır
  const MAX_MS = 5 * 60 * 1000

  useEffect(() => {
    let tick
    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        streamRef.current = stream
        const mr = new MediaRecorder(stream)
        mediaRef.current = mr
        chunksRef.current = []
        mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data) }
        mr.start()
        startedRef.current = Date.now()

        // Seviye örnekleme — mobildeki dBFS eşleniği
        const ctx = new (window.AudioContext || window.webkitAudioContext)()
        audioCtxRef.current = ctx
        const src = ctx.createMediaStreamSource(stream)
        const an = ctx.createAnalyser()
        an.fftSize = 512
        src.connect(an)
        const buf = new Uint8Array(an.frequencyBinCount)
        let last = 0
        const loop = () => {
          rafRef.current = requestAnimationFrame(loop)
          const now = Date.now()
          if (now - last < 160) return
          last = now
          an.getByteTimeDomainData(buf)
          let peak = 0
          for (let i = 0; i < buf.length; i++) peak = Math.max(peak, Math.abs(buf[i] - 128) / 128)
          const lv = Math.round(Math.min(1, peak * 1.8) * 9)
          levelsRef.current.push(lv)
          if (levelsRef.current.length > 200) levelsRef.current.shift()
          setLevels([...levelsRef.current.slice(-40)])
        }
        loop()

        tick = setInterval(() => {
          const e = Date.now() - startedRef.current
          setMs(e)
          if (e >= MAX_MS) finish()
        }, 100)
      } catch {
        toast.error('Mikrofon açılamadı', 'Tarayıcı izni verilmemiş olabilir')
        onCancel?.()
      }
    }
    start()
    return () => {
      clearInterval(tick)
      cleanup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cleanup = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    try { audioCtxRef.current?.close() } catch { /* kapalı olabilir */ }
    streamRef.current?.getTracks().forEach((t) => t.stop())
  }

  const waveform = () => {
    const src = levelsRef.current
    if (!src.length) return '2'.repeat(40)
    const step = src.length / 40
    let out = ''
    for (let i = 0; i < 40; i++) {
      const a = Math.floor(i * step), b = Math.min(src.length, Math.ceil((i + 1) * step))
      let sum = 0, n = 0
      for (let k = a; k < b; k++) { sum += src[k]; n++ }
      out += n ? Math.min(9, Math.max(0, Math.round(sum / n))) : 2
    }
    return out
  }

  const finish = () => {
    if (doneRef.current) return
    doneRef.current = true
    const mr = mediaRef.current
    const elapsed = Date.now() - startedRef.current
    if (!mr) { onCancel?.(); return }
    mr.onstop = () => {
      cleanup()
      // 1 saniyeden kısa kayıt gönderilmez — yanlışlıkla tıklama
      if (elapsed < 1000) {
        toast.warning('Çok kısa', 'Konuşmaya başlayınca gönder')
        onCancel?.()
        return
      }
      const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' })
      const ext = (mr.mimeType || '').includes('ogg') ? 'ogg' : 'webm'
      const file = new File([blob], `sesli-mesaj.${ext}`, { type: blob.type })
      onDone?.(file, elapsed, waveform())
    }
    try { mr.stop() } catch { onCancel?.() }
  }

  const abort = () => {
    doneRef.current = true
    try { mediaRef.current?.stop() } catch { /* zaten durmuş */ }
    cleanup()
    onCancel?.()
  }

  const time = `${String(Math.floor(ms / 60000)).padStart(2, '0')}:${String(Math.floor(ms / 1000) % 60).padStart(2, '0')}`

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 border border-gray-200 rounded-xl bg-white">
      <Mic size={16} className="text-red-500 animate-pulse shrink-0" />
      <span className="text-[13px] font-semibold text-gray-800 tabular-nums">{time}</span>
      <div className="flex-1 flex items-center gap-[2px] h-6">
        {levels.map((l, i) => (
          <div key={i} className="flex-1 rounded-sm bg-primary-500"
            style={{ height: `${3 + l * 2}px` }} />
        ))}
      </div>
      <button type="button" onClick={abort} title="İptal"
        className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50">
        <Trash2 size={16} />
      </button>
      <button type="button" onClick={finish} title="Gönder"
        className="p-1.5 rounded-lg text-primary-600 hover:bg-primary-50">
        <Send size={16} />
      </button>
    </div>
  )
}
